export type ProviderId = "youtube" | "vimeo" | "twitch" | "loom" | "wistia";

type AllowedParams = Record<string, string>;

interface ProviderSpec {
  id: ProviderId;
  hostMatches: RegExp[];
  extractId(url: URL): string | null;
  buildEmbedUrl(id: string, params: AllowedParams): string;
  allowedParams: readonly string[];
}

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{6,20}$/;
const VIMEO_ID_RE = /^\d+$/;
const TWITCH_VIDEO_ID_RE = /^\d+$/;
const TWITCH_CHANNEL_RE = /^[A-Za-z0-9_]{4,25}$/;
const LOOM_ID_RE = /^[a-f0-9]{32}$/i;
const WISTIA_ID_RE = /^[a-z0-9]{8,12}$/i;

const PROVIDERS: ProviderSpec[] = [
  {
    id: "youtube",
    hostMatches: [/(?:^|\.)youtube(?:-nocookie)?\.com$/i, /^youtu\.be$/i],
    extractId(url) {
      const path = url.pathname;
      // /embed/<id>
      const embedMatch = path.match(/^\/embed\/([A-Za-z0-9_-]+)/);
      if (embedMatch) {
        const id = embedMatch[1] ?? "";
        return YOUTUBE_ID_RE.test(id) ? id : null;
      }
      // youtu.be/<id>
      if (/^youtu\.be$/i.test(url.hostname)) {
        const id = path.slice(1);
        return YOUTUBE_ID_RE.test(id) ? id : null;
      }
      // /watch?v=<id>
      const v = url.searchParams.get("v");
      if (v) return YOUTUBE_ID_RE.test(v) ? v : null;
      return null;
    },
    buildEmbedUrl(id, params) {
      // YouTube returns "Error 153 / Video player configuration error" when
      // the embedding page can't be validated. Stamp `origin` and
      // `widget_referrer` after caller params so they cannot be overridden.
      const sp = new URLSearchParams({ ...params });
      sp.set("origin", "https://www.denker.ai");
      sp.set("widget_referrer", "https://www.denker.ai");
      return `https://www.youtube-nocookie.com/embed/${id}?${sp.toString()}`;
    },
    allowedParams: [
      "t",
      "start",
      "autoplay",
      "loop",
      "mute",
      "controls",
      "enablejsapi",
      "playsinline",
      "rel",
    ],
  },
  {
    id: "vimeo",
    hostMatches: [/(?:^|\.)vimeo\.com$/i],
    extractId(url) {
      // /video/<id> or /<id>
      const match = url.pathname.match(/^(?:\/video)?\/(\d+)/);
      if (!match) return null;
      const id = match[1] ?? "";
      return VIMEO_ID_RE.test(id) ? id : null;
    },
    buildEmbedUrl(id, params) {
      const sp = new URLSearchParams({ ...params });
      const qs = sp.toString();
      return `https://player.vimeo.com/video/${id}${qs ? `?${qs}` : ""}`;
    },
    allowedParams: ["h", "dnt", "autoplay", "loop", "muted"],
  },
  {
    id: "twitch",
    hostMatches: [/(?:^|\.)twitch\.tv$/i],
    extractId(url) {
      // player.twitch.tv uses ?video= or ?channel=
      const video = url.searchParams.get("video");
      if (video) return TWITCH_VIDEO_ID_RE.test(video) ? video : null;
      const channel = url.searchParams.get("channel");
      if (channel)
        return TWITCH_CHANNEL_RE.test(channel) ? `channel:${channel}` : null;
      // twitch.tv/videos/<id>
      const videoPath = url.pathname.match(/^\/videos\/(\d+)/);
      if (videoPath) {
        const vid = videoPath[1] ?? "";
        return TWITCH_VIDEO_ID_RE.test(vid) ? vid : null;
      }
      // twitch.tv/<channel>
      const channelPath = url.pathname.match(/^\/([A-Za-z0-9_]{4,25})$/);
      if (channelPath) return `channel:${channelPath[1] ?? ""}`;
      return null;
    },
    buildEmbedUrl(id, params) {
      const sp = new URLSearchParams();
      if (id.startsWith("channel:")) {
        sp.set("channel", id.slice(8));
      } else {
        sp.set("video", id);
      }
      for (const [k, v] of Object.entries(params)) {
        sp.set(k, v);
      }
      // Twitch requires parent= to match the embedding host. Set last so a
      // caller-supplied parent (or future allowlist additions) cannot override.
      sp.set("parent", "denker.ai");
      return `https://player.twitch.tv/?${sp.toString()}`;
    },
    allowedParams: ["autoplay", "muted", "time"],
  },
  {
    id: "loom",
    hostMatches: [/(?:^|\.)loom\.com$/i],
    extractId(url) {
      const match = url.pathname.match(/^\/(?:share|embed)\/([a-f0-9]{32})/i);
      if (!match) return null;
      const id = match[1] ?? "";
      return LOOM_ID_RE.test(id) ? id : null;
    },
    buildEmbedUrl(id, params) {
      const sp = new URLSearchParams({ ...params });
      const qs = sp.toString();
      return `https://www.loom.com/embed/${id}${qs ? `?${qs}` : ""}`;
    },
    allowedParams: ["hide_owner", "hide_share", "hideEmbedTopBar", "t"],
  },
  {
    id: "wistia",
    hostMatches: [/(?:^|\.)wistia\.(?:com|net)$/i],
    extractId(url) {
      // /embed/iframe/<id> or /medias/<id>
      const match = url.pathname.match(
        /^\/(?:embed\/iframe|medias)\/([a-z0-9]+)/i,
      );
      if (!match) return null;
      const id = match[1] ?? "";
      return WISTIA_ID_RE.test(id) ? id : null;
    },
    buildEmbedUrl(id, params) {
      const sp = new URLSearchParams({ ...params });
      const qs = sp.toString();
      return `https://fast.wistia.net/embed/iframe/${id}${qs ? `?${qs}` : ""}`;
    },
    allowedParams: ["autoPlay", "silentAutoPlay"],
  },
];

function findProvider(hostname: string): ProviderSpec | null {
  const lower = hostname.toLowerCase();
  return (
    PROVIDERS.find((p) => p.hostMatches.some((re) => re.test(lower))) ?? null
  );
}

export function detectProvider(url: string): ProviderId | null {
  try {
    const parsed = new URL(url);
    const spec = findProvider(parsed.hostname);
    return spec?.id ?? null;
  } catch {
    return null;
  }
}

export function extractEmbedParams(
  url: string,
): { provider: ProviderId; id: string; params: AllowedParams } | null {
  try {
    const parsed = new URL(url);
    const spec = findProvider(parsed.hostname);
    if (!spec) return null;
    const id = spec.extractId(parsed);
    if (!id) return null;
    const params: AllowedParams = {};
    for (const key of spec.allowedParams) {
      const val = parsed.searchParams.get(key);
      if (val !== null) params[key] = val;
    }
    return { provider: spec.id, id, params };
  } catch {
    return null;
  }
}

export function buildProxyUrl({
  provider,
  id,
  params,
}: {
  provider: ProviderId;
  id: string;
  params: AllowedParams;
}): string {
  const sp = new URLSearchParams({ provider, id, ...params });
  return `https://www.denker.ai/embed?${sp.toString()}`;
}

/**
 * Given a provider id and a validated id + params, return the canonical
 * provider embed URL. Used by both the frontend (web path) and the
 * landing proxy page (server side).
 */
export function buildEmbedUrl(
  provider: ProviderId,
  id: string,
  params: AllowedParams,
): string {
  const spec = PROVIDERS.find((p) => p.id === provider);
  if (!spec) throw new Error(`Unknown provider: ${provider}`);
  return spec.buildEmbedUrl(id, params);
}

const DIRECT_YOUTUBE_EMBED_REFERRER = "https://space.denker.ai";

export function isTauriDevHttpOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const { protocol, hostname, port } = window.location;
  return (
    protocol === "http:" &&
    port === "3012" &&
    (hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "[::1]" ||
      hostname === "::1")
  );
}

export function directYoutubeReferrer(): string {
  return isTauriDevHttpOrigin()
    ? window.location.origin
    : DIRECT_YOUTUBE_EMBED_REFERRER;
}

export function normalizeDirectYoutubeEmbedSrc(src: string): string {
  try {
    const url = new URL(src, window.location.href);
    const isYouTube = /(?:^|\.)youtube(?:-nocookie)?\.com$/i.test(
      url.hostname,
    );
    if (!isYouTube) return src;

    if (url.hostname.toLowerCase() !== "www.youtube-nocookie.com") {
      url.hostname = "www.youtube-nocookie.com";
    }
    const referrer = directYoutubeReferrer();
    url.searchParams.set("origin", referrer);
    url.searchParams.set("widget_referrer", referrer);
    return url.toString();
  } catch {
    return src;
  }
}
